import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import { Autocomplete, Typography, AutocompleteProps } from "@mui/material";
import React, { ReactElement } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { Checkbox } from "~frontend/components/Checkbox";
import FormTextField from "~frontend/components/forms/FormTextField";
import { palette } from "~frontend/theme";

export type AutocompleteValue = {
  id: string;
  label: string;
};

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = (
  <CheckBox fontSize="small" sx={{ color: palette.darkGreen }} />
);

export function RhfMultiSelectCheckbox<T extends FieldValues>(
  props: UseControllerProps<T> &
    Omit<
      AutocompleteProps<AutocompleteValue, true, false, false>,
      "renderInput"
    > & {
      bottomLabel?: string;
      label: string;
    }
): ReactElement {
  const { name, label, control, bottomLabel, ...rest } = props;
  const { field, fieldState } = useController({
    control,
    name,
  });

  return (
    <Autocomplete
      {...rest}
      disableCloseOnSelect
      renderValue={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          return (
            <Typography variant="body1" component="span" key={key} {...rest}>
              {option.id}
              {index < value.length - 1 ? "," : ""}
            </Typography>
          );
        })
      }
      multiple
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          key={props.key}
          style={
            selected
              ? {
                  backgroundColor: palette.lightGray,
                  boxShadow: "0px 0px 0px 2px white inset",
                }
              : undefined
          }
        >
          <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
          <Typography variant="body2">{option.label}</Typography>
        </li>
      )}
      renderInput={params => (
        <FormTextField
          {...params}
          error={!!fieldState?.error?.message}
          helperText={fieldState?.error?.message ?? bottomLabel}
          variant="outlined"
          size="small"
          sx={{ mb: 0, mt: 0, pr: 0 }}
          label={label}
        />
      )}
      value={props.options.filter(({ id }) => field.value.includes(id))}
      onChange={(_, options) => {
        field.onChange(options.map(o => o.id));
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "preventOverflow",
              enabled: true,
            },
          ],
        },
      }}
    />
  );
}
